/**
 * THE AVATAR LIBRARY (§ F120, 2026-08-21).
 *
 * Jake: *"I want all players to be able to pick from a list of like 40 avatars.
 * Classic, weird, or let them draw one that is stored in our database and
 * used."*
 *
 * So: forty glyphs, in two halves that are labelled as Jake described them —
 * CLASSIC (the ones a family picker would expect: a cat, a rocket, a crown)
 * and WEIRD (the ones that make a nine-year-old pick them: a ghost, a skull, a
 * biohazard). They are lucide glyphs, drawn on the player's own session colour,
 * which keeps them consistent with every other icon in the app and costs no new
 * assets — the same reasoning `game-marks.tsx` records for the game marks.
 *
 * The ID IS THE CONTRACT: it is what the database stores (`players.avatar_preset`)
 * and it must never be reused for a different glyph, or somebody's avatar
 * silently becomes another one. Adding is free; renaming is not.
 */

import type { LucideIcon } from "lucide-react";
import {
  Anchor, Apple, Atom, Axe, Banana, Bird, Bomb, Bone, Brain, Bug,
  Cake, Cat, Cherry, Croissant, Crown, Dog, Drama, Feather, Fish, Flame,
  Ghost, Grape, Guitar, Heart, IceCream, Leaf, Moon, Mountain, Music, Pizza,
  Rabbit, Rocket, Skull, Snail, Sparkles, Star, Sun, Turtle, Umbrella, Zap,
} from "lucide-react";

/** Every preset is a lucide glyph — same set the whole app draws with. */
export type AvatarGlyph = LucideIcon;

export interface AvatarPreset {
  id: string;
  label: string;
  glyph: AvatarGlyph;
  group: "classic" | "weird";
}

/** Forty, in the two groups Jake named. Order is the order they are drawn in. */
export const AVATAR_PRESETS: readonly AvatarPreset[] = [
  // ── classic ───────────────────────────────────────────────────────────────
  { id: "cat", label: "Cat", glyph: Cat, group: "classic" },
  { id: "dog", label: "Dog", glyph: Dog, group: "classic" },
  { id: "rabbit", label: "Rabbit", glyph: Rabbit, group: "classic" },
  { id: "bird", label: "Bird", glyph: Bird, group: "classic" },
  { id: "fish", label: "Fish", glyph: Fish, group: "classic" },
  { id: "turtle", label: "Turtle", glyph: Turtle, group: "classic" },
  { id: "star", label: "Star", glyph: Star, group: "classic" },
  { id: "sun", label: "Sun", glyph: Sun, group: "classic" },
  { id: "moon", label: "Moon", glyph: Moon, group: "classic" },
  { id: "heart", label: "Heart", glyph: Heart, group: "classic" },
  { id: "crown", label: "Crown", glyph: Crown, group: "classic" },
  { id: "rocket", label: "Rocket", glyph: Rocket, group: "classic" },
  { id: "guitar", label: "Guitar", glyph: Guitar, group: "classic" },
  { id: "music", label: "Music", glyph: Music, group: "classic" },
  { id: "leaf", label: "Leaf", glyph: Leaf, group: "classic" },
  { id: "mountain", label: "Mountain", glyph: Mountain, group: "classic" },
  { id: "anchor", label: "Anchor", glyph: Anchor, group: "classic" },
  { id: "umbrella", label: "Umbrella", glyph: Umbrella, group: "classic" },
  { id: "feather", label: "Feather", glyph: Feather, group: "classic" },
  { id: "sparkles", label: "Sparkles", glyph: Sparkles, group: "classic" },
  // ── weird ─────────────────────────────────────────────────────────────────
  { id: "ghost", label: "Ghost", glyph: Ghost, group: "weird" },
  { id: "skull", label: "Skull", glyph: Skull, group: "weird" },
  { id: "brain", label: "Brain", glyph: Brain, group: "weird" },
  { id: "bug", label: "Bug", glyph: Bug, group: "weird" },
  { id: "snail", label: "Snail", glyph: Snail, group: "weird" },
  { id: "bone", label: "Bone", glyph: Bone, group: "weird" },
  { id: "bomb", label: "Bomb", glyph: Bomb, group: "weird" },
  { id: "flame", label: "Flame", glyph: Flame, group: "weird" },
  { id: "zap", label: "Zap", glyph: Zap, group: "weird" },
  { id: "atom", label: "Atom", glyph: Atom, group: "weird" },
  { id: "axe", label: "Axe", glyph: Axe, group: "weird" },
  { id: "drama", label: "Drama", glyph: Drama, group: "weird" },
  { id: "pizza", label: "Pizza", glyph: Pizza, group: "weird" },
  { id: "cake", label: "Cake", glyph: Cake, group: "weird" },
  { id: "ice-cream", label: "Ice cream", glyph: IceCream, group: "weird" },
  { id: "croissant", label: "Croissant", glyph: Croissant, group: "weird" },
  { id: "banana", label: "Banana", glyph: Banana, group: "weird" },
  { id: "apple", label: "Apple", glyph: Apple, group: "weird" },
  { id: "cherry", label: "Cherry", glyph: Cherry, group: "weird" },
  { id: "grape", label: "Grape", glyph: Grape, group: "weird" },
] as const;

const BY_ID = new Map(AVATAR_PRESETS.map((preset) => [preset.id, preset]));

/** null → the id is unknown (an avatar from a newer build, or junk). */
export function avatarPreset(id: string | null | undefined): AvatarPreset | null {
  return id ? (BY_ID.get(id) ?? null) : null;
}
